import 'react-datepicker/dist/react-datepicker.css'
import './WeeklyPicker.css'

import { ChevronUpDownIcon } from '@heroicons/react/24/outline'
import { cn } from '@sparkai/utils'
import { endOfWeek, format, getYear, startOfDay, startOfToday, subDays, subWeeks } from 'date-fns'
import {
  ComponentPropsWithoutRef,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import ReactDatePicker from 'react-datepicker'

import { DateInterval } from '../DatePicker/DatePicker'
import { WeeklyPickerHeader } from './WeeklyPickerHeader'

type DateRangeTuple = [Date | null, Date | null]

export interface WeeklyPickerProps {
  value?: DateRangeTuple
  onChange: (date: DateRangeTuple) => void
  placeholder?: string

  showTimeSelect?: boolean
  timeIntervals?: number
  monthsShown?: number
  showTimeInput?: boolean

  excludeDates?: Date[]
  excludeDateIntervals?: DateInterval[]
  filterDate?: (date: Date) => boolean

  excludeTimes?: Date[]
  filterTime?: (date: Date) => boolean

  shouldCloseOnSelect?: boolean
  showMonthAndYearSelector?: boolean
  timeFormat?: string
  dateFormat?: string

  maxDate?: Date | null
  minDate?: Date | null

  className?: string
  weeksDefaultRange?: number
  error?: boolean

  hideInput?: boolean
  autoShow?: boolean
  calendarTheme?: 'full' | 'shadow'
}

export const getWeeksRange = (weeks: number): [Date, Date] => {
  const today = startOfToday()
  const lastDayOfWeek = endOfWeek(today)
  const first = startOfDay(subDays(subWeeks(lastDayOfWeek as Date, weeks), -1))

  return [first, lastDayOfWeek]
}

export function WeeklyPicker({
  value,
  onChange,
  placeholder,
  weeksDefaultRange = 8,
  error,
  hideInput,
  autoShow,
  calendarTheme = 'shadow',
  ...props
}: WeeklyPickerProps) {
  const startDate = value?.[0]
  const endDate = value?.[1]
  const datePickerRef = useRef<ReactDatePicker>(null)

  const [isRangeSelected, setIsRangeSelected] = useState(false)

  const weekLabel = `${weeksDefaultRange} weeks`

  const formatPattern =
    endDate && startDate && getYear(endDate) - getYear(startDate) > 0 ? 'dd MMM yyyy' : 'dd MMM'

  const periodLabel =
    startDate && endDate ? `${format(startDate, formatPattern)} - ${format(endDate, formatPattern)}` : ''

  const range = getWeeksRange(weeksDefaultRange)

  useEffect(() => {
    onChange(range)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className='date-weekly-picker-root'>
      <ReactDatePicker
        ref={datePickerRef}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        selected={endDate}
        showPopperArrow={false}
        customInput={hideInput ? <></> : <CustomInputSelect error={error} />}
        calendarClassName={cn({
          'w-full border-0 mt-[-20px]': calendarTheme === 'full',
          'shadow-xl ring-1 ring-black ring-opacity-5 p-6 border-0 rounded-md': calendarTheme === 'shadow',
        })}
        onChange={(update) => {
          if (onChange && update) {
            const selectedDate = update as unknown as DateRangeTuple
            const lastDayOfWeek = endOfWeek(selectedDate[0] as Date)
            const first = startOfDay(subDays(subWeeks(lastDayOfWeek as Date, weeksDefaultRange), -1))
            const newPeriod = [first, lastDayOfWeek] as DateRangeTuple
            onChange(newPeriod)
            setIsRangeSelected(false)
          }
        }}
        startDate={startDate}
        renderCustomHeader={(props) => (
          <WeeklyPickerHeader
            onClickRangeDates={(value) => {
              setIsRangeSelected(true)
              onChange && onChange(value as unknown as DateRangeTuple)
              !autoShow && datePickerRef?.current?.setOpen(false)
            }}
            isRangeSelected={isRangeSelected}
            rangeWeek={weeksDefaultRange}
            startDate={startDate}
            endDate={endDate}
            {...props}
          />
        )}
        monthsShown={1}
        endDate={endDate}
        placeholderText={placeholder}
        selectsRange
        open={autoShow}
        {...props}
      >
        {periodLabel && (
          <div className='ml-3'>
            <label className='font-normal text-brand-gray-500'>Selected ({weekLabel}):</label>
            <label className='ml-9'>{periodLabel}</label>
          </div>
        )}
      </ReactDatePicker>
    </div>
  )
}

export type CustomInputSelectProps = ComponentPropsWithoutRef<'button'> & {
  error?: boolean
}

const CustomInputSelect = forwardRef<HTMLButtonElement, CustomInputSelectProps>(function CustomInputSelect(
  { value, error, ...props },
  ref
) {
  const innerRef = useRef<HTMLButtonElement>(null)
  // Just a trick to use ref while also forwarding
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  useImperativeHandle(ref, () => innerRef.current!)

  const valueFormatted = useMemo(() => {
    if (!value) return value

    const splitValues = (value as string).split('-')
    const start = new Date(splitValues[0].trim())
    const end = new Date(splitValues[1].trim())

    const diffYears = getYear(end) - getYear(start)
    const formatPattern = diffYears > 0 ? 'dd MMM yyyy' : 'dd MMM'

    return `${format(start, formatPattern)} - ${format(end, formatPattern)}`
  }, [value])

  return (
    <div
      className={cn(
        'chromatic-ignore mt-1 flex w-full min-w-[180px] cursor-pointer items-center justify-between rounded-md border border-brand-gray-300 bg-white px-3 py-2 font-inter text-sm text-brand-gray-900 shadow-sm',
        {
          'border border-brand-red-400': error,
        }
      )}
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        innerRef.current?.focus()
      }}
      aria-label='open calendar modal'
    >
      <button ref={innerRef} {...props}>
        {valueFormatted}
      </button>

      <ChevronUpDownIcon className='h-5 w-5 font-bold text-brand-gray-400' aria-hidden='true' />
    </div>
  )
})
